import type { IDBPTransaction } from "@dumbmatter/idb";
import {
	draft,
	finances,
	freeAgents,
	league,
	player,
	season,
	team,
} from "../index.ts";
import {
	DEFAULT_STADIUM_CAPACITY,
	DEPTH_CHART_NAME,
	LEAGUE_DATABASE_VERSION,
	PHASE,
	PLAYER,
	REAL_PLAYERS_INFO,
} from "../../../common/constants.ts";
import type {
	Conditions,
	Conf,
	Div,
	GameAttributesLeague,
	GameAttributesLeagueWithHistory,
	GetLeagueOptions,
	League,
	Phase,
	Player,
	PlayerWithoutKey,
	RealPlayerPhotos,
	RealTeamInfo,
	Team,
	TeamBasic,
	TeamSeasonWithoutKey,
	TeamStatsWithoutKey,
} from "../../../common/types.ts";
import type { NewLeagueTeam } from "../../../ui/views/NewLeague/types.ts";
import { CUMULATIVE_OBJECTS } from "../../api/leagueFileUpload.ts";
import { Cache, connectLeague, idb } from "../../db/index.ts";
import {
	helpers,
	local,
	lock,
	logEvent,
	toUI,
	updatePhase,
	updateStatus,
} from "../../util/index.ts";
import g, { wrapNewValueIfCurrentlyWrapped } from "../../util/g.ts";
import type { Settings } from "../../views/settings.ts";
import { getAutoTicketPriceByTid } from "../game/attendance.ts";
import addRelatives from "../realRosters/addRelatives.ts";
import loadDataBasketball from "../realRosters/loadData.basketball.ts";
import addDraftProspects from "./create/addDraftProspects.ts";
import createRandomPlayers from "./create/createRandomPlayers.ts";
import getRealTeamPlayerData from "./create/getRealTeamPlayerData.ts";
import createGameAttributes from "./createGameAttributes.ts";
import initRandomDebutsForRandomPlayersLeague from "./initRandomDebutsForRandomPlayersLeague.ts";
import setRepeatSeason from "./setRepeatSeason.ts";
import processPlayerNewLeague from "./processPlayerNewLeague.ts";
import remove from "./remove.ts";
import { TOO_MANY_TEAMS_TOO_SLOW } from "../season/getInitialNumGamesConfDivSettings.ts";
import { DEFAULT_LEVEL, amountToLevel } from "../../../common/budgetLevels.ts";
import {
	colaUpdate,
	upgradeGamesVersion65,
	type LeagueDBStoreNames,
} from "../../db/connectLeague.ts";
import type { NewLeagueSettings } from "../../views/newLeague.ts";
import { getNumPlayersTradedAwayNormalized } from "../player/getNumPlayersTradedAwayNormalized.ts";
import { applyRealTeamInfo } from "../../../common/applyRealTeamInfo.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { last } from "../../../common/utils.ts";
import { newLeagueGodModeLimits } from "../../util/newLeagueGodModeLimits.ts";
import { choice, shuffle } from "../../../common/random.ts";

export type TeamInfo = TeamBasic & {
	disabled?: boolean;
	stadiumCapacity?: number;
	seasons?: TeamSeasonWithoutKey[];
	stats?: TeamStatsWithoutKey[];
};

// Doesn't seem to make a difference no matter what this is, but keeping at 1 makes the progress bar nicer
export const highWaterMark = 1;

const addLeagueMeta = async ({
	lid,
	name,
	teams,
	tid,
}: {
	lid: number;
	name: string;
	teams: Team[];
	tid: number;
}) => {
	const t = teams[tid]!;

	const l: League = {
		lid,
		name,
		tid,
		phaseText: "",
		teamName: t.name,
		teamRegion: t.region,
		heartbeatID: undefined,
		heartbeatTimestamp: undefined,
		difficulty: g.get("difficulty"),
		created: new Date(),
		lastPlayed: new Date(),
		startingSeason: g.get("startingSeason"),
		season: g.get("season"),
		imgURL: t.imgURLSmall ?? t.imgURL,
	};

	// In case we are importing over an old league
	const oldLeague = await idb.meta.get("leagues", lid);
	await remove(lid);
	if (oldLeague) {
		l.created = oldLeague.created;
		l.starred = oldLeague.starred;
	}

	await idb.meta.add("leagues", l);

	const { bindLeagueConnection } = await import("../../db/ensureLeagueDb.ts");
	const leagueDb = await connectLeague(lid);
	bindLeagueConnection(leagueDb, lid);
};

class Buffer {
	MAX_BUFFER_SIZE: number;
	keptKeys: Set<string>;
	keys: Set<LeagueDBStoreNames>;
	rows: [LeagueDBStoreNames, any][];
	previousTransaction: IDBPTransaction<any, any, any> | undefined;

	constructor(keptKeys: Set<string>) {
		this.MAX_BUFFER_SIZE = 10000;
		this.keptKeys = keptKeys;

		this.keys = new Set();
		this.rows = [];
	}

	addRow(row: [LeagueDBStoreNames, any]) {
		const key = row[0];

		if (!this.keptKeys.has(key)) {
			return;
		}

		this.keys.add(key);
		this.rows.push(row);
	}

	private clear() {
		this.keys = new Set();
		this.rows = [];
	}

	isFull() {
		return this.rows.length >= this.MAX_BUFFER_SIZE;
	}

	// This is so flush does not have to wait for the writes to complete before we start filling the buffer again
	async waitForPreviousTransaction() {
		if (this.previousTransaction) {
			await this.previousTransaction.done;
		}
	}

	async flush() {
		await this.waitForPreviousTransaction();

		if (this.keys.size > 0) {
			const transaction = idb.league.transaction(
				Array.from(this.keys),
				"readwrite",
			);

			for (const row of this.rows) {
				transaction.objectStore(row[0]).put(row[1]);
			}

			this.previousTransaction = transaction;

			this.clear();
		}
	}

	async finalize() {
		await this.flush();
		await this.waitForPreviousTransaction();
	}
}

export type PreProcessParams = {
	activeTids: number[];
	averagePopulation: number | undefined;
	hasRookieContracts: boolean | undefined;
	noStartingInjuries: boolean;
	realPlayerPhotos: RealPlayerPhotos | undefined;
	realTeamInfo: RealTeamInfo | undefined;
	scoutingLevel: number;
	version: number | undefined;
};

// These obejcts have auto incrementing primary keys that aren't referenced by anything else, so it's safe to delete them when importing, and also prevents any issues from people manually editing league files and accidentally repeating a primary key, resulting in the latest one overwriting any earlier ones
const PRIMARY_KEYS_TO_DELETE: Record<string, string> = {
	messages: "mid",
	playerFeats: "fid",
	releasedPlayers: "rid",
	scheduledEvents: "id",
	teamSeasons: "rid",
	teamStats: "rid",
};

const preProcess = async (
	key: string,
	x: any,
	{
		activeTids,
		averagePopulation,
		hasRookieContracts,
		noStartingInjuries,
		realPlayerPhotos,
		realTeamInfo,
		scoutingLevel,
		version,
	}: PreProcessParams,
) => {
	const primaryKeyToDelete = PRIMARY_KEYS_TO_DELETE[key];
	if (primaryKeyToDelete !== undefined) {
		delete x[primaryKeyToDelete];
	}

	if (key === "draftPicks") {
		if (typeof x.pick !== "number") {
			x.pick = 0;
		}
	} else if (key === "games") {
		// Fix missing +/-, blocks against in boxscore
		if (isSport("basketball")) {
			if (x.teams[0].ba === undefined) {
				x.teams[0].ba = 0;
				x.teams[1].ba = 0;
			}
			for (const t of x.teams) {
				for (const p of t.players) {
					p.ba ??= 0;
					p.pm ??= 0;
				}
			}
		}
	} else if (key === "players") {
		x = await processPlayerNewLeague({
			p: x,
			activeTids,
			hasRookieContracts,
			noStartingInjuries,
			realPlayerPhotos,
			scoutingLevel,
			version,
		});
	} else if (key === "scheduledEvents") {
		if (averagePopulation !== undefined) {
			if (x.type === "expansionDraft") {
				for (const t of x.info.teams) {
					t.pop = averagePopulation;
				}
			} else if (x.type === "teamInfo" && x.info.pop !== undefined) {
				x.info.pop = averagePopulation;
			}
		}

		// This is not really needed, since applyRealTeamInfo is called again in processScheduledEvents. It's just to make it look more normal in the database, for when I eventually build a GUI editor for scheduled events.
		if (realTeamInfo) {
			if (x.type === "expansionDraft") {
				for (const t of x.info.teams) {
					applyRealTeamInfo(t, realTeamInfo, x.season);
				}
			} else if (x.type === "teamInfo") {
				applyRealTeamInfo(x.info, realTeamInfo, x.season);
			}
		}
	} else if (key === "playerFeats") {
		// Version 60 upgrade
		const score = x.score;
		if (
			x.won !== undefined &&
			x.result === undefined &&
			typeof score === "string"
		) {
			const pts = score.split("-").map((y) => Number.parseInt(y));
			let diff = -Infinity;
			if (
				pts[0] !== undefined &&
				!Number.isNaN(pts[0]) &&
				pts[1] !== undefined &&
				!Number.isNaN(pts[1])
			) {
				diff = pts[0] - pts[1];
			}

			x.result = diff === 0 ? "T" : x.won ? "W" : "L";

			delete x.won;
		}
	} else if (key === "playoffSeries") {
		// Version 69 upgrade
		if (x.byConf === true) {
			x.byConf = 2;
		}
	}

	return x;
};

type ExtraFromStream = {
	activePlayers: PlayerWithoutKey[];
	hasEvents: boolean;
	teamHasRosterOrder: Set<number>;
};

const getSaveToDB = async ({
	keptKeys,
	maxGid,
	preProcessParams,
	setLeagueCreationStatus,
}: {
	keptKeys: Set<LeagueDBStoreNames>;
	maxGid: number | undefined;
	preProcessParams: PreProcessParams;
	setLeagueCreationStatus: CreateStreamProps["setLeagueCreationStatus"];
}) => {
	const buffer = new Buffer(keptKeys);

	const extraFromStream: ExtraFromStream = {
		activePlayers: [],
		hasEvents: false,
		teamHasRosterOrder: new Set(),
	};

	let currentScheduleGid = maxGid ?? -1;

	let prevKey: LeagueDBStoreNames | undefined;

	let currentPid = -1;

	const writableStream = new WritableStream<{
		key: LeagueDBStoreNames;
		value: any;
	}>(
		{
			async write(chunk) {
				const { key, value } = chunk;

				if (CUMULATIVE_OBJECTS.has(key) || key === "teams") {
					// Currently skipped:
					// - meta because it doesn't get written to DB
					// - gameAttributes/startingSeason/version/teams because we already have it from basicInfo.
					return;
				}

				if (key !== prevKey) {
					// console.timeLog("createStream");
					// console.log("loading", key);
					setLeagueCreationStatus(`Processing ${key}...`);
					prevKey = key;
				}

				// Overwrite schedule with known safe gid (higher than any game) in case it is somehow conflicting with games, because schedule gids are not referenced anywhere else but game gids are
				if (key === "schedule" && keptKeys.has("schedule")) {
					currentScheduleGid += 1;
					value.gid = currentScheduleGid;
				}

				if (key === "events" && keptKeys.has("events")) {
					extraFromStream.hasEvents = true;
				}

				const isPlayers = key === "players" && keptKeys.has("players");

				if (isPlayers) {
					if (value.pid === undefined) {
						currentPid += 1;
						value.pid = currentPid;
					} else if (value.pid > currentPid) {
						currentPid = value.pid;
					}
				}

				if (keptKeys.has(key)) {
					const processed = await preProcess(key, value, preProcessParams);

					if (
						isPlayers &&
						(processed.tid >= PLAYER.UNDRAFTED ||
							processed.tid === PLAYER.UNDRAFTED_FANTASY_TEMP)
					) {
						extraFromStream.activePlayers.push(processed);

						if (
							!isSport("basketball") ||
							typeof value.rosterOrder === "number"
						) {
							extraFromStream.teamHasRosterOrder.add(value.tid);
						}
					} else {
						buffer.addRow([key, processed]);
						if (buffer.isFull()) {
							await buffer.flush();
						}
					}
				}
			},

			async close() {
				await buffer.finalize();
			},
		},
		new CountQueuingStrategy({
			highWaterMark,
		}),
	);

	return {
		extraFromStream,
		saveToDB: writableStream,
	};
};

const finalizeStartingSeason = (
	startingSeasonFromFile: number | undefined,
	startingSeasonFromInput: string | undefined,
) => {
	if (startingSeasonFromFile !== undefined) {
		return startingSeasonFromFile;
	}

	if (startingSeasonFromInput) {
		const startingSeason2 = Number.parseInt(startingSeasonFromInput);
		if (!Number.isNaN(startingSeason2)) {
			return startingSeason2;
		}
	}

	return new Date().getFullYear();
};

type GameAttributeOverrides = Partial<Record<keyof GameAttributesLeague, any>>;

const finalizeGameAttributes = async ({
	conditions,
	gameAttributes,
	gameAttributeOverrides,
	getLeagueOptions,
	randomization,
	startingSeason,
	teamsCameFromFile,
	teamInfos,
	tid,
	version,
}: {
	conditions?: Conditions;
	gameAttributes: any;
	gameAttributeOverrides: GameAttributeOverrides;
	getLeagueOptions: GetLeagueOptions | undefined;
	randomization:
		| "none"
		| "shuffle"
		| "debuts"
		| "debutsKeepCurrent"
		| "debutsForever"
		| "debutsForeverKeepCurrent";
	startingSeason: number;
	teamsCameFromFile: boolean;
	teamInfos: TeamInfo[];
	tid: number;
	version: number | undefined;
}) => {
	const finalized = {
		...gameAttributes,
	};

	for (const key of helpers.keys(gameAttributeOverrides)) {
		let phase = finalized.phase ?? PHASE.PRESEASON;
		if (phase < 0) {
			if (finalized.nextPhase !== undefined) {
				phase = finalized.nextPhase;
			}
		}

		// If we're overriding a value with history, keep the history
		finalized[key] = wrapNewValueIfCurrentlyWrapped(
			finalized,
			key,
			gameAttributeOverrides[key],
			{
				season: finalized.season ?? startingSeason,
				phase,
			},
		);
	}

	if (
		(randomization === "debutsForever" ||
			randomization === "debutsForeverKeepCurrent") &&
		finalized.randomDebutsForever === undefined
	) {
		finalized.randomDebutsForever = 1;
	}

	if (
		getLeagueOptions?.type === "real" &&
		getLeagueOptions?.realStats === "all"
	) {
		let start = finalized.season;
		if (getLeagueOptions.phase > PHASE.PLAYOFFS) {
			start += 1;
		}

		// startingSeason is 1947, so use userTid history to denote when user actually started managing team
		finalized.userTid = [
			{ start: -Infinity, value: PLAYER.DOES_NOT_EXIST },
			{
				start,
				value: tid,
			},
		];
	}

	// Check if we need to set godModeInPast because some custom teams are too powerful
	if (!teamsCameFromFile) {
		// Only for new leagues, not created from file!

		let godModeInPastOverride = false;
		const godModeLimits = newLeagueGodModeLimits();
		for (const t of teamInfos) {
			if (t.pop > godModeLimits.pop) {
				godModeInPastOverride = true;
				break;
			}
			if (
				t.stadiumCapacity !== undefined &&
				t.stadiumCapacity > godModeLimits.stadiumCapacity
			) {
				godModeInPastOverride = true;
				break;
			}
		}
		if (godModeInPastOverride) {
			finalized.godModeInPast = true;
		}
	}

	// Also mutates teamInfos
	const finalized2 = await createGameAttributes(
		{
			gameAttributesInput: finalized,
			startingSeason,
			teamInfos,
			userTid: tid,
			version,
		},
		conditions,
	);

	return finalized2;
};

const finalizeDBExceptPlayers = async ({
	teamSeasons,
	teamStats,
	teams,
}: {
	teamSeasons: TeamSeasonWithoutKey[];
	teamStats: TeamStatsWithoutKey[];
	teams: Team[];
}) => {
	const tx = idb.league.transaction(
		["games", "schedule", "teamSeasons", "teamStats", "teams", "trade"],
		"readwrite",
	);

	const tradeStore = tx.objectStore("trade");
	const trade = await tradeStore.get(0);
	if (!trade) {
		const tid = g.get("userTid");
		await tradeStore.add({
			rid: 0,
			teams: [
				{
					tid,
					pids: [],
					pidsExcluded: [],
					dpids: [],
					dpidsExcluded: [],
				},
				{
					// Load initial trade view with the lowest-numbered non-user team (so, either 0 or 1).
					tid: tid === 0 ? 1 : 0,
					pids: [],
					pidsExcluded: [],
					dpids: [],
					dpidsExcluded: [],
				},
			],
		});
	}

	// Handle schedule with no "day" property
	const scheduleStore = tx.objectStore("schedule");
	const schedule = await scheduleStore.getAll();
	if (schedule.length > 0) {
		const missingDay = schedule.some(
			(matchup) => typeof matchup.day !== "number",
		);

		if (missingDay) {
			const gamesThisSeason = await tx
				.objectStore("games")
				.index("season")
				.getAll(g.get("season"));

			const updatedSchedule = season.addDaysToSchedule(
				schedule,
				gamesThisSeason,
			);

			for (const game of updatedSchedule) {
				await scheduleStore.put(game);
			}
		}
	}

	const teamsStore = tx.objectStore("teams");
	for (const t of teams) {
		await teamsStore.put(t);
	}

	const teamSeasonsStore = tx.objectStore("teamSeasons");
	for (const ts of teamSeasons) {
		await teamSeasonsStore.put(ts);
	}

	const teamStatsStore = tx.objectStore("teamStats");
	for (const ts of teamStats) {
		await teamStatsStore.put(ts);
	}
};

const confirmSequential = (objs: any, key: string, objectName: string) => {
	const values = new Set();

	for (const obj of objs) {
		const value = obj[key];

		if (typeof value !== "number") {
			throw new Error(`Missing or invalid ${key} for ${objectName}`);
		}

		values.add(value);
	}

	for (let i = 0; i < values.size; i++) {
		if (!values.has(i)) {
			throw new Error(
				`${key} values must be sequential with no gaps starting from 0, but no ${objectName} has a value of ${i}`,
			);
		}
	}

	return values;
};

const processTeamInfos = async ({
	gameAttributes,
	realTeamInfo,
	teamInfos,
	userTid,
	version,
}: {
	gameAttributes: {
		salaryCap: number;
		season: number;
	};
	realTeamInfo: RealTeamInfo | undefined;
	teamInfos: any[];
	userTid: number;
	version: number | undefined;
}) => {
	const { salaryCap, season } = gameAttributes;

	if (realTeamInfo) {
		for (const t of teamInfos) {
			applyRealTeamInfo(t, realTeamInfo, season);

			// This is especially needed for new real players leagues started after the regular season. Arguably makes sense to always do, for consistency, since applyRealTeamInfo will override the current logos anyway, might as well do the historical ones too. But let's be careful.
			if (t.seasons) {
				for (const teamSeason of t.seasons) {
					applyRealTeamInfo(teamSeason, realTeamInfo, teamSeason.season, {
						srIDOverride: teamSeason.srID ?? t.srID,
					});
				}
			}
		}
	}

	// If these are teams uploaded from a sport with depth (like FBGM) to a sport without depth (like BBGM) need to remove depth
	if (DEPTH_CHART_NAME === undefined) {
		for (const t of teamInfos) {
			if (Object.hasOwn(t, "depth")) {
				delete t.depth;
			}
		}
	}

	// Version 55 upgrade
	for (const t of teamInfos) {
		if (t.budget) {
			for (const key of Object.keys(t.budget)) {
				const value = t.budget[key];
				if (typeof value !== "number") {
					if (key === "ticketPrice") {
						t.budget[key] = value.amount;
					} else {
						t.budget[key] = amountToLevel(
							value?.amount ?? DEFAULT_LEVEL,
							salaryCap,
						);
					}
				}
			}

			// initialBudget will be created in team.generate below
		}

		// Version 72 upgrade
		colaUpdate(t);
	}

	// Version 68 upgrade
	if (version !== undefined && version < 68) {
		const keys = ["imgURL", "imgURLSmall"] as const;
		for (const t of teamInfos) {
			for (const key of keys) {
				if (t[key] === "/img/logos-primary/CHI.svg") {
					t[key] = "/img/logos-primary/CHW.svg";
				} else if (t[key] === "/img/logos-secondary/CHI.svg") {
					t[key] = "/img/logos-secondary/CHW.svg";
				}
			}
		}
	}

	// Version 69 upgrade
	if (version !== undefined && version < 69) {
		for (const t of teamInfos) {
			if (t.pop === undefined || t.stadiumCapacity === undefined) {
				const teamSeason = t.seasons?.at(-1);
				t.pop = teamSeason?.pop ?? 1;
				t.stadiumCapacity = teamSeason?.pop ?? DEFAULT_STADIUM_CAPACITY;
			}
		}
	}

	const teams = teamInfos.map((t) => team.generate(t));

	// Version 55 upgrade
	const budgetsByTid: Record<number, Team["budget"]> = {};
	for (const t of teams) {
		budgetsByTid[t.tid] = t.budget;
	}

	const teamSeasons: TeamSeasonWithoutKey[] = [];
	const teamStats: TeamStatsWithoutKey[] = [];

	const teamSeasonUniqueKeys = new Set();
	const teamSeasonUniqueKeyErrorCounts: Record<string, number> = {};

	let scoutingLevel: number | undefined;

	for (const [i, t] of teams.entries()) {
		const teamInfo = teamInfos[i];
		let teamSeasonsLocal: TeamSeasonWithoutKey[];

		if (teamInfo.seasons) {
			teamSeasonsLocal = teamInfo.seasons;
			const last = teamSeasonsLocal.at(-1);

			// This used to check for `last.season !== g.get("season")` but that is a false positive when re-activating teams in the offseason such as with an expansion draft
			if (
				last &&
				(last.season > g.get("season") || last.season === undefined) &&
				!t.disabled
			) {
				last.season = g.get("season");

				// Remove any past seasons that claim to be from this season or a future season
				teamSeasonsLocal = [
					...teamSeasonsLocal.filter((ts) => ts.season < last.season),
					last,
				];
			}

			for (const teamSeason of teamSeasonsLocal) {
				// Version 55 upgrade
				// Move the amount to root, no more storing rank
				for (const type of ["revenues", "expenses"] as const) {
					if (teamSeason[type]) {
						for (const key of Object.keys(teamSeason[type])) {
							const value = (teamSeason[type] as any)[key];
							if (typeof value !== "number") {
								(teamSeason[type] as any)[key] = value?.amount ?? 0;
							}
						}
					}
				}
				if (!teamSeason.expenseLevels) {
					// Compute historical expense levels, assuming budget was the same as it is now. In theory could come up wtih a better estimate from expenses, but historical salary cap data is not stored so it wouldn't be perfect, and also who cares
					const expenseLevelsKeys = [
						"coaching",
						"facilities",
						"health",
						"scouting",
					] as const;
					teamSeason.expenseLevels = {} as any;
					const gp = helpers.getTeamSeasonGp({
						// This is needed in case teamSeason is still partial
						won: teamSeason.won ?? 0,
						lost: teamSeason.lost ?? 0,
						tied: teamSeason.tied ?? 0,
						otl: teamSeason.otl ?? 0,
					});
					for (const key of expenseLevelsKeys) {
						// Careful, teamSeason.tid might not be defined for imported leagues yet!
						teamSeason.expenseLevels[key] =
							gp * (budgetsByTid[t.tid]?.[key] ?? DEFAULT_LEVEL);
					}
				}

				// Version 68 upgrade
				if (version !== undefined && version < 68) {
					const keys = ["imgURL", "imgURLSmall"] as const;
					for (const key of keys) {
						if (teamSeason[key] === "/img/logos-primary/CHI.svg") {
							teamSeason[key] = "/img/logos-primary/CHW.svg";
						} else if (teamSeason[key] === "/img/logos-secondary/CHI.svg") {
							teamSeason[key] = "/img/logos-secondary/CHW.svg";
						}
					}
				}

				// See similar code in teamsPlus
				const copyFromTeamIfUndefined = [
					// For upgrades, or manually edited league files
					"cid",
					"did",
					"region",
					"name",
					"abbrev",
					"imgURL",
					"colors",
					"jersey",

					// These ones too, they might be on team object but for very old leagues they aren't. This is not for upgrades, since these being on the root is new, they were always on teamSeason
					"pop",
					"stadiumCapacity",
				] as const;
				for (const key of copyFromTeamIfUndefined) {
					// @ts-expect-error
					teamSeason[key] ??= t[key];
				}

				const defaultZero = [
					"gpHome",
					"att",
					"won",
					"lost",
					"tied",
					"otl",
					"wonHome",
					"lostHome",
					"tiedHome",
					"otlHome",
					"wonAway",
					"lostAway",
					"tiedAway",
					"otlAway",
					"wonDiv",
					"lostDiv",
					"tiedDiv",
					"otlDiv",
					"wonConf",
					"lostConf",
					"tiedConf",
					"otlConf",
					"streak",
					"payrollEndOfSeason",
					"numPlayersTradedAway",
				] as const;
				for (const key of defaultZero) {
					teamSeason[key] ??= 0;
				}

				// Keep these in sync with genSeasonRow
				teamSeason.cash ??= 10000;
				teamSeason.hype ??= Math.random();
				teamSeason.pop ??= 1;
				teamSeason.stadiumCapacity ??= DEFAULT_STADIUM_CAPACITY;
				teamSeason.playoffRoundsWon ??= -1;
				teamSeason.lastTen ??= [];
				teamSeason.ownerMood ??= {
					wins: 0,
					playoffs: 0,
					money: 0,
				};
				teamSeason.revenues ??= {
					luxuryTaxShare: 0,
					merch: 0,
					sponsor: 0,
					ticket: 0,
					nationalTv: 0,
					localTv: 0,
				};
				teamSeason.expenses ??= {
					salary: 0,
					luxuryTax: 0,
					minTax: 0,
					scouting: 0,
					coaching: 0,
					health: 0,
					facilities: 0,
				};
				teamSeason.expenseLevels ??= {
					scouting: 0,
					coaching: 0,
					health: 0,
					facilities: 0,
				};
			}
		} else if (!t.disabled) {
			const row = team.genSeasonRow(t);
			row.pop = teamInfo.pop;
			row.stadiumCapacity = teamInfo.stadiumCapacity;
			teamSeasonsLocal = [row];
		} else {
			teamSeasonsLocal = [];
		}

		for (const teamSeason of teamSeasonsLocal) {
			teamSeason.tid = t.tid;

			if (typeof teamSeason.stadiumCapacity !== "number") {
				teamSeason.stadiumCapacity =
					t.stadiumCapacity !== undefined
						? t.stadiumCapacity
						: g.get("defaultStadiumCapacity");
			}

			// If this is specified in a league file, we can ignore it because they should all be in order, and sometimes people manually edit the file and include duplicates
			delete teamSeason.rid;

			// teamSeasons = teamSeasons.filter(ts2 => ts2.season !== teamSeason.season);
			teamSeasons.push(teamSeason);

			// Check for uniqueness of (tid, season) otherwise it's an error when saving to IndexedDB
			const uniqueKey = `${teamSeason.tid}/${teamSeason.season}`;
			if (teamSeasonUniqueKeys.has(uniqueKey)) {
				teamSeasonUniqueKeyErrorCounts[uniqueKey] ??= 1;
				teamSeasonUniqueKeyErrorCounts[uniqueKey] += 1;
			}
			teamSeasonUniqueKeys.add(uniqueKey);
		}

		const teamStatsLocal: TeamStatsWithoutKey[] = teamInfo.stats ?? [];

		for (const ts of teamStatsLocal) {
			ts.tid = t.tid;

			if (isSport("basketball")) {
				if (
					(typeof ts.oppBlk !== "number" || Number.isNaN(ts.oppBlk)) &&
					ts.ba !== undefined
				) {
					ts.oppBlk = ts.ba;
					delete ts.ba;
				}

				if (typeof ts.oppBlk !== "number" || Number.isNaN(ts.oppBlk)) {
					ts.oppBlk = 0;
				}
			}

			// teamStats = teamStats.filter(ts2 => ts2.season !== ts.season);
			teamStats.push(ts);
		}

		// Save scoutingLevel for later
		if (i === userTid) {
			scoutingLevel = await finances.getLevelLastThree("scouting", {
				t,
				teamSeasons: teamSeasonsLocal,
			});
		}
	}

	{
		const errors = Object.entries(teamSeasonUniqueKeyErrorCounts);
		if (errors.length > 0) {
			throw new Error(
				`Duplicate team season entries for the following tid/season combinations: ${errors.map(([key, count]) => `${key}${count > 2 ? ` (${count} times)` : ""}`).join(", ")}`,
			);
		}
	}

	if (scoutingLevel === undefined) {
		throw new Error("scoutingLevel should be defined");
	}

	return {
		scoutingLevel,
		teams,
		teamSeasons,
		teamStats,
	};
};

const finalizeActivePlayers = async ({
	fileHasPlayers,
	phase,
}: {
	fileHasPlayers: boolean;
	phase: Phase;
}) => {
	// If no players were uploaded in custom league file, add some relatives!
	if (!fileHasPlayers && g.get("numTeams") < TOO_MANY_TEAMS_TOO_SLOW) {
		const players0 = await idb.cache.players.getAll();
		for (const p of players0) {
			await player.addRelatives(p);
		}
	}

	// A new loop, because addRelatives updates the database
	const players1 = await idb.cache.players.getAll();
	for (const p of players1) {
		if (fileHasPlayers) {
			// Fix jersey numbers, which matters for league files where that data might be invalid (conflicts) or incomplete. Important to do this here at the end, so incomplete jersey numbers can be filled in with full knowledge of any teammate jersey numbers, otherwise you might randomly pick one on import and have that unknowingly conflict with a manually specified one
			if (
				p.tid >= 0 &&
				p.jerseyNumber === undefined &&
				phase <= PHASE.PLAYOFFS
			) {
				player.setJerseyNumber(p, await player.genJerseyNumber(p));
			}
		}

		await player.updateValues(p);
		await idb.cache.players.put(p);
	}

	const pidsToNormalize = players1
		.filter((p) => p.contract.temp)
		.map((p) => p.pid);
	await freeAgents.normalizeContractDemands({
		type: "newLeague",
		pids: pidsToNormalize,
	});

	// WHY IS THIS NEEDED? Can't use players0 because the addRelatives call above might make a copy of a player object and write it to the cache, in which case the prior objects for those players in players0 will be stale.
	const players = await idb.cache.players.getAll();

	// Adjustment for hard cap - lower contracts for teams above cap
	if (!fileHasPlayers && g.get("salaryCapType") === "hard") {
		const minContract = g.get("minContract");

		const teams = await idb.cache.teams.getAll();
		for (const t of teams) {
			if (t.disabled) {
				continue;
			}
			const roster = players.filter((p) => p.tid === t.tid);
			let payroll = roster.reduce((total, p) => total + p.contract.amount, 0);

			while (payroll > g.get("salaryCap")) {
				let foundAny = false;

				for (const p of roster) {
					if (p.contract.amount >= minContract + 10) {
						payroll -= 10;
						p.contract.amount -= 10;
						foundAny = true;
					} else if (p.contract.amount > minContract) {
						payroll -= p.contract.amount - minContract;
						p.contract.amount = minContract;
						foundAny = true;
					}
				}

				if (!foundAny) {
					throw new Error(
						"Invalid combination of salaryCapType, salaryCap, and minContract - a team full of min contract players is still over the hard cap",
					);
				}
			}
		}
	}

	for (const p of players) {
		if (p.tid >= 0 && p.salaries.length === 0) {
			player.setContract(p, p.contract, true);
		}

		if ((p as any).rookieContract) {
			p.contract.rookie = true;
			delete (p as any).rookieContract;
		}

		delete p.contract.temp;

		// Maybe not needed, but let's be sure
		await idb.cache.players.put(p);
	}
};

type CreateStreamProps = {
	conditions?: Conditions;
	confs: Conf[];
	divs: Div[];
	fromFile: {
		gameAttributes: Record<string, unknown> | undefined;
		hasRookieContracts: boolean;
		maxGid: number | undefined;
		startingSeason: number | undefined;
		teams: any[] | undefined;
		version: number | undefined;
	};
	getLeagueOptions: GetLeagueOptions | undefined;
	keptKeys: Set<LeagueDBStoreNames>;
	lid: number;
	name: string;
	setLeagueCreationStatus: (status: string) => void;
	settings: NewLeagueSettings;
	shuffleRosters: boolean;
	startingSeasonFromInput: string | undefined;
	teamsFromInput: NewLeagueTeam[];
	tid: number;
};

const beforeDBStream = async ({
	conditions,
	confs,
	divs,
	fromFile,
	getLeagueOptions,
	keptKeys,
	lid,
	name,
	settings,
	startingSeasonFromInput,
	teamsFromInput,
	tid,
}: Pick<
	CreateStreamProps,
	| "conditions"
	| "confs"
	| "divs"
	| "fromFile"
	| "getLeagueOptions"
	| "keptKeys"
	| "lid"
	| "name"
	| "settings"
	| "startingSeasonFromInput"
	| "teamsFromInput"
	| "tid"
>) => {
	// These wouldn't be needed here, except the beforeView logic is fucked up
	lock.reset();
	local.reset();

	// Single out all the weird settings that don't go directly into gameAttributes
	const {
		noStartingInjuries,
		randomization,
		repeatSeason,

		// realStats is already in getLeagueOptions
		realStats,

		...otherSettings
	} = settings;

	const gameAttributeOverrides: GameAttributeOverrides = {
		...otherSettings,
		confs,
		divs,
	};

	// This setting is allowed to be undefined, so make it that way when appropriate
	if (
		!REAL_PLAYERS_INFO &&
		(!getLeagueOptions || getLeagueOptions.type !== "real")
	) {
		delete gameAttributeOverrides.realDraftRatings;
	}

	const startingSeason = finalizeStartingSeason(
		fromFile.startingSeason,
		startingSeasonFromInput,
	);

	const filteredFromFile = {
		teams:
			!!fromFile.teams && keptKeys.has("teams") ? fromFile.teams : undefined,
		gameAttributes:
			!!fromFile.gameAttributes && keptKeys.has("gameAttributes")
				? fromFile.gameAttributes
				: undefined,
	};

	let teamInfos = helpers.addPopRank(filteredFromFile.teams ?? teamsFromInput);
	for (const t of teamInfos) {
		for (const key of ["imgURL", "imgURLSmall"] as const) {
			if (typeof t[key] === "string") {
				t[key] = helpers.stripBbcode(t[key]);
			}
		}
	}

	// Validation of some identifiers
	confirmSequential(teamInfos, "tid", "team");

	const gameAttributes = await finalizeGameAttributes({
		conditions,
		// Use fromFile.gameAttributes in addition to gameAttributeOverrides because it preserves history and any non-standard settings
		gameAttributes: filteredFromFile.gameAttributes ?? {},
		gameAttributeOverrides,
		getLeagueOptions,
		randomization,
		startingSeason,
		teamsCameFromFile: !!filteredFromFile.teams,
		teamInfos,
		tid,
		version: fromFile.version,
	});

	let averagePopulation: number | undefined;
	if (gameAttributes.equalizeRegions) {
		let totalPopulation = 0;
		for (const t of teamInfos) {
			totalPopulation += t.pop;
		}

		// Round to 2 digits
		averagePopulation =
			Math.round((totalPopulation / teamInfos.length) * 100) / 100;

		for (const t of teamInfos) {
			t.pop = averagePopulation;

			const last = t.seasons?.at(-1);
			if (last) {
				last.pop = t.pop;
			}
		}
		teamInfos = helpers.addPopRank(teamInfos);
	}

	const { realPlayerPhotos, realTeamInfo } = await getRealTeamPlayerData(
		{
			fileHasPlayers:
				keptKeys.has("players") ||
				teamInfos.some((t) => t.usePlayers) ||
				randomization === "debuts" ||
				randomization === "debutsKeepCurrent" ||
				randomization === "debutsForever" ||
				randomization === "debutsForeverKeepCurrent",
			fileHasTeams: !!filteredFromFile.teams,
		},
		conditions,
	);

	// Hacky - put gameAttributes in g so they can be seen by functions called from this function
	helpers.resetG();
	gameAttributes.lid = lid;
	Object.assign(g, gameAttributes);

	// Needs to be done after g is set
	const { scoutingLevel, teams, teamSeasons, teamStats } =
		await processTeamInfos({
			gameAttributes,
			realTeamInfo,
			teamInfos,
			userTid: tid,
			version: fromFile.version,
		});

	// Update after applying real team info and any other changes in processTeamInfos
	gameAttributes.teamInfoCache = teams.map((t) => ({
		abbrev: t.abbrev,
		disabled: t.disabled,
		imgURL: t.imgURL,
		imgURLSmall: t.imgURLSmall,
		name: t.name,
		region: t.region,
	}));

	const activeTids = teams.filter((t) => !t.disabled).map((t) => t.tid);

	await addLeagueMeta({
		lid,
		name,
		teams,
		tid,
	});

	return {
		activeTids,
		averagePopulation,
		gameAttributes,
		noStartingInjuries,
		realPlayerPhotos,
		realTeamInfo,
		repeatSeason,
		scoutingLevel,
		teamInfos,
		teamSeasons,
		teamStats,
		teams,
	};
};

const adjustSeasonPlayer = (p: Partial<PlayerWithoutKey>) => {
	const season = g.get("season");

	const playerSeason = p.ratings!.at(-1)!.season;

	const diff = season - playerSeason;

	if (diff !== 0) {
		const keys = ["awards", "injuries", "ratings", "salaries"] as const;
		for (const key of keys) {
			if (p[key]) {
				for (const row of p[key]!) {
					row.season += diff;
				}
			}
		}

		p.born!.year += diff;

		if (p.draft) {
			p.draft.year += diff;
		}
	}
};

const afterDBStream = async ({
	activeTids,
	extraFromStream,
	fromFile,
	hasRookieContracts,
	gameAttributes,
	getLeagueOptions,
	lid,
	noStartingInjuries,
	randomization,
	realPlayerPhotos,
	repeatSeason,
	scoutingLevel,
	shuffleRosters,
	teamInfos,
	teamSeasons,
	teamStats,
	teams,
}: {
	extraFromStream: ExtraFromStream;
	hasRookieContracts: boolean;
	noStartingInjuries: boolean;
	randomization: Settings["randomization"];
	realPlayerPhotos: RealPlayerPhotos | undefined;
} & Pick<
	CreateStreamProps,
	"fromFile" | "getLeagueOptions" | "lid" | "shuffleRosters"
> &
	Pick<
		Awaited<ReturnType<typeof beforeDBStream>>,
		| "activeTids"
		| "gameAttributes"
		| "repeatSeason"
		| "scoutingLevel"
		| "teamInfos"
		| "teamSeasons"
		| "teamStats"
		| "teams"
	>) => {
	if (shuffleRosters) {
		// Assign the team ID of all players to the 'playerTids' array.
		// Check tid to prevent draft prospects from being swapped with established players
		const numPlayersToShuffle = extraFromStream.activePlayers.filter(
			(p) => p.tid > PLAYER.FREE_AGENT,
		).length;

		const playerTids = [];
		while (playerTids.length < numPlayersToShuffle) {
			// Shuffle each set of tids individually, because if we did all at once, rosters might wind up unbalanced
			const shuffled = [...activeTids];
			shuffle(shuffled);
			playerTids.push(...shuffled);
		}

		for (const p of extraFromStream.activePlayers) {
			p.transactions = [];

			if (p.tid > PLAYER.FREE_AGENT) {
				p.tid = playerTids.pop()!;

				if (p.stats && p.stats.length > 0) {
					p.stats.at(-1).tid = p.tid;

					if (p.statsTids) {
						p.statsTids.push(p.tid);
					} else {
						p.statsTids = [p.tid];
					}
				}
			}
		}
	}

	const fileHasPlayers = extraFromStream.activePlayers.length > 0;

	// Version 70 upgrade
	if (fileHasPlayers) {
		let numPlayersTradedAwayNormalized: Record<number, number> | undefined;
		for (const p of extraFromStream.activePlayers) {
			if (p.tid !== PLAYER.FREE_AGENT || p.numPlayersTradedAwayNormalized) {
				continue;
			}

			if (!numPlayersTradedAwayNormalized) {
				numPlayersTradedAwayNormalized = {};
				const teamSeasonsByTid = Object.groupBy(
					teamSeasons.filter((row) => row.season >= gameAttributes.season - 2),
					(row) => row.tid,
				);
				for (const t of teams) {
					if (t.disabled) {
						continue;
					}

					numPlayersTradedAwayNormalized[t.tid] =
						getNumPlayersTradedAwayNormalized(
							teamSeasonsByTid[t.tid] ?? [],
							gameAttributes.season,
						);
				}
			}

			p.numPlayersTradedAwayNormalized = numPlayersTradedAwayNormalized;
		}
	}

	const randomDebuts =
		randomization === "debuts" ||
		randomization === "debutsKeepCurrent" ||
		randomization === "debutsForever" ||
		randomization === "debutsForeverKeepCurrent";

	let activePlayers = fileHasPlayers
		? extraFromStream.activePlayers
		: await createRandomPlayers({
				activeTids,
				onlyFreeAgents: randomDebuts,
				scoutingLevel,
				teams,
			});

	// If players are specified for some team on import (from CustomizeTeams), replace the randomly generated players
	const replaceTids = new Set();
	const extraActivePlayers: PlayerWithoutKey[] = [];
	const draftPickAutoContract = g.get("draftPickAutoContract");
	const rookieSalaries = draftPickAutoContract ? draft.getRookieSalaries() : [];
	for (const t of teamInfos) {
		if (t.usePlayers && t.players) {
			replaceTids.add(t.tid);

			for (const p of t.players) {
				delete p.pid;
				delete p.relatives;
				delete p.transactions;
				delete p.contract;
				delete p.salaries;
				delete p.awards;
				delete p.hof;
				delete p.diedYear;
				p.gamesUntilTradable = 0;
				p.ptModifier = 1;
				p.stats = [];
				p.tid = t.tid;
				adjustSeasonPlayer(p);

				const p2 = await processPlayerNewLeague({
					p,
					activeTids,
					hasRookieContracts,
					noStartingInjuries,
					realPlayerPhotos,
					scoutingLevel,
					version: LEAGUE_DATABASE_VERSION,
				});

				// This may not be ideal for a season before the rookie contract scale existed, but whatever
				if ((p2 as any).rookieContract && draftPickAutoContract) {
					const pickIndex =
						(p2.draft.round - 1) * g.get("numActiveTeams") + p2.draft.pick - 1;
					player.setContract(
						p2,
						{
							amount: rookieSalaries[pickIndex] ?? rookieSalaries.at(-1)!,
							exp: p2.contract.exp,
						},
						true,
					);
					p2.contract.rookie = true;
				}

				// Do this after processPlayerNewLeague so it can impute rookie contract status based on draft round
				if (p2.draft) {
					p2.draft = {
						...p2.draft,
						tid: -1,
						originalTid: -1,
						round: 0,
						pick: 0,
					};
				}

				extraActivePlayers.push(p2);
			}
		}
	}
	if (replaceTids.size > 0) {
		activePlayers = activePlayers.filter((p) => !replaceTids.has(p.tid));
		activePlayers.push(...extraActivePlayers);
	}

	// Run this for random debuts only in random players leagues, since in real players leagues it's done in getLeague already
	if (
		(!getLeagueOptions || getLeagueOptions.type === "legends") &&
		randomDebuts
	) {
		// Delete any random players that are somehow in draft class, such as if this is an imported league
		activePlayers = activePlayers.filter((p) => p.tid !== PLAYER.UNDRAFTED);

		const basketball = await loadDataBasketball();

		const realPlayers = await initRandomDebutsForRandomPlayersLeague({
			activeTids,
			players: activePlayers,
			basketball,
			numActiveTeams: gameAttributes.numActiveTeams,
			realDraftRatings: gameAttributes.realDraftRatings ?? "rookie",
			phase: gameAttributes.phase,
			season: gameAttributes.season,
		});
		for (const p of realPlayers) {
			const p2 = await processPlayerNewLeague({
				p,
				activeTids,
				hasRookieContracts,
				noStartingInjuries,
				realPlayerPhotos,
				scoutingLevel,
				version: LEAGUE_DATABASE_VERSION,
			});
			last(p2.ratings).season = gameAttributes.season;
			activePlayers.push(p2);
		}
	}

	if (activePlayers.some((p) => p.srID !== undefined)) {
		const basketball = await loadDataBasketball();

		// Depending on how we got to this point, there may already be pids and here (normal real players league) or there may not (individual team selected from Customize Teams, or maybe random debuts in random players league)
		let maxPid = 0;
		for (const p of activePlayers) {
			if (p.pid !== undefined && p.pid > maxPid) {
				maxPid = p.pid;
			}
		}
		for (const p of activePlayers) {
			if (p.pid === undefined) {
				maxPid += 1;
				p.pid = maxPid;
			}
		}

		// This is redundant for a normal real players league, but oh well, it's not very slow. Can't get rid of it in getLeague because that runs on all players, not just active. Can't get rid of it here because it's needed for other types of leagues
		addRelatives(activePlayers as unknown as Player[], basketball.relatives);
	}

	// For random debuts we don't want addDraftProspects to be called, since it will fill in with random players. However this does imply that future pick value is going to be messed up for those transition years between random debuts generations, since getPickValues does not support partial draft classes.
	if (!randomDebuts) {
		await addDraftProspects({
			players: activePlayers,
			scoutingLevel,
		});
	}

	// Unless we got strategy from a league file, calculate it here
	for (const [i, t] of teams.entries()) {
		if (teamInfos[i].strategy === undefined) {
			const teamPlayers = activePlayers
				.filter((p) => p.tid === i)
				.map((p) => ({
					pid: p.pid,
					injury: p.injury,
					value: p.value,
					ratings: last(p.ratings),
				}));
			const ovr = team.ovr(teamPlayers);
			t.strategy = ovr >= 60 ? "contending" : "rebuilding";
		}
	}

	// Write final versions of everything to the DB, except active players because some post-processing uses functions that read from the cache
	await finalizeDBExceptPlayers({
		teamSeasons,
		teamStats,
		teams,
	});

	// Clear old game attributes from g, to make sure the new ones are saved to the db in setGameAttributes
	helpers.resetG();
	g.setWithoutSavingToDB("lid", lid);
	await toUI("resetLeague", []);

	if (idb.cache) {
		idb.cache.stopAutoFlush();
	}

	idb.cache = new Cache();
	idb.cache.newLeague = true;
	await idb.cache.fill(gameAttributes.season);

	const gameAttributesToUpdate: Partial<GameAttributesLeagueWithHistory> = {
		...gameAttributes,
	};

	// Hack! Need to not include lid in the update here, because then it gets sent to the UI and is seen in Controller before the URL changes, which interferes with running beforeLeague when the first view in the new league is loaded. lol
	delete gameAttributesToUpdate.lid;

	// This gets put on in createGameAttributes, but we don't want to write it to DB
	g.setWithoutSavingToDB("teamInfoCache", gameAttributes.teamInfoCache);
	delete gameAttributesToUpdate.teamInfoCache;

	// Need this before calling setGameAttributes, so the "cola" draftType can see recent top draft picks
	for (const p of activePlayers) {
		await idb.cache.players.put(p);
	}

	// Write gameAttributes to DB in special way, to get extra functionality from setGameAttributes and because it's not in the database native format in leagueData (object, not array like others).
	// BUT - league.setGameAttributes is not expecting gameAttributes with history, so this could break in subtle ways in the future!
	await league.setGameAttributes(gameAttributesToUpdate as any, {
		// This is needed or some real draft picks may be overwritten
		skipDraftGenPicks: true,
	});

	await finalizeActivePlayers({
		fileHasPlayers,
		phase: gameAttributes.phase,
	});

	// Handle repeatSeason after creating league, so we know what random players were created
	const currentRepeatSeasonType = g.get("repeatSeason")?.type ?? "disabled";
	if (repeatSeason !== currentRepeatSeasonType) {
		await setRepeatSeason(repeatSeason);
	}

	const skipNewPhase = fromFile.gameAttributes?.phase !== undefined;
	const realPlayers = !!getLeagueOptions;

	if (!skipNewPhase || realPlayers) {
		await updatePhase();
		await updateStatus("Idle");

		// Auto sort rosters
		for (const t of teams) {
			if (t.disabled) {
				continue;
			}

			// If league file has players with roster order set, don't auto sort even if skipNewPhase is false
			if (
				!extraFromStream.teamHasRosterOrder.has(t.tid) ||
				!g.get("userTids").includes(t.tid)
			) {
				await team.rosterAutoSort(t.tid);
			}
		}
	}

	if (g.get("phase") === PHASE.PLAYOFFS) {
		await season.newSchedulePlayoffsDay();
	}

	await draft.genPicks({
		realPlayers,
	});

	if (!extraFromStream.hasEvents) {
		await logEvent({
			text: "Welcome to your new league!",
			type: "newLeague",
			tids: [g.get("userTid")],
			showNotification: false,
			score: 20,
		});
	}

	{
		const teams = await idb.cache.teams.getAll();
		for (const t of teams) {
			if (!t.disabled && t.autoTicketPrice !== false) {
				t.budget.ticketPrice = await getAutoTicketPriceByTid(t.tid);
			}
		}
	}

	// Set numDraftPicksCurrent, for upgrading leagues
	if (g.get("phase") === PHASE.DRAFT) {
		const currentDraftPicks = await draft.getOrder();
		const draftNotStarted =
			currentDraftPicks.every((dp) => dp.round === 0) ||
			currentDraftPicks.some((dp) => dp.round === 1 && dp.pick === 1);
		if (draftNotStarted) {
			const numDraftPicksCurrent = currentDraftPicks.length;
			if (numDraftPicksCurrent > 0) {
				await league.setGameAttributes({
					numDraftPicksCurrent,
				});
			}
		}
	}

	// Memorials
	if (!fileHasPlayers && !g.get("playerBioInfo")) {
		let memorials;
		if (isSport("hockey")) {
			// https://discord.com/channels/@me/896580823057326130
			memorials = [
				{
					region: "Boston",
					firstName: "Matthew",
					lastName: "Dennison",
					bornLoc: "Rhode Island, USA",
				},
			];
		}

		if (memorials) {
			for (const memorial of memorials) {
				const tid = teams.find((t) => t.region === memorial.region)?.tid;
				if (tid !== undefined) {
					const players = (
						await idb.cache.players.indexGetAll("playersByTid", tid)
					).filter((p) => p.relatives.length === 0);
					const p = choice(players);
					if (p) {
						p.firstName = memorial.firstName;
						p.lastName = memorial.lastName;
						p.born.loc = memorial.bornLoc;
						p.real = true;
						await idb.cache.players.put(p);
					}
				}
			}
		}
	}

	await idb.cache.flush();
	idb.cache.startAutoFlush();
	local.leagueLoaded = true;

	// Version 65 upgrade
	if (fromFile.version !== undefined && fromFile.version < 65) {
		const transaction = idb.league.transaction(
			["games", "playerFeats", "playoffSeries"],
			"readwrite",
		);
		await upgradeGamesVersion65({
			transaction,
			stopIfTooMany: true,
			lid,
		});
	}
};

const createStream = async (
	stream: ReadableStream,
	{
		conditions,
		confs,
		divs,
		fromFile,
		getLeagueOptions,
		keptKeys,
		lid,
		name,
		setLeagueCreationStatus,
		settings,
		shuffleRosters,
		startingSeasonFromInput,
		teamsFromInput, // use if none in file
		tid,
	}: CreateStreamProps,
) => {
	// console.time("createStream");
	const {
		activeTids,
		averagePopulation,
		gameAttributes,
		noStartingInjuries,
		realPlayerPhotos,
		realTeamInfo,
		repeatSeason,
		scoutingLevel,
		teamInfos,
		teamSeasons,
		teamStats,
		teams,
	} = await beforeDBStream({
		conditions,
		confs,
		divs,
		fromFile,
		getLeagueOptions,
		keptKeys,
		lid,
		name,
		settings,
		startingSeasonFromInput,
		teamsFromInput,
		tid,
	});
	// console.timeLog("createStream");

	const { extraFromStream, saveToDB } = await getSaveToDB({
		keptKeys,
		maxGid: fromFile.maxGid,
		preProcessParams: {
			activeTids,
			averagePopulation,
			hasRookieContracts: fromFile.hasRookieContracts,
			noStartingInjuries,
			realPlayerPhotos,
			realTeamInfo,
			scoutingLevel,
			version: fromFile.version,
		},
		setLeagueCreationStatus,
	});
	// console.timeLog("createStream");

	await stream.pipeTo(saveToDB);
	// console.timeLog("createStream");

	setLeagueCreationStatus("Finalizing...");

	await afterDBStream({
		activeTids,
		extraFromStream,
		fromFile,
		gameAttributes,
		getLeagueOptions,
		hasRookieContracts: fromFile.hasRookieContracts,
		lid,
		noStartingInjuries,
		randomization: settings.randomization,
		realPlayerPhotos,
		repeatSeason,
		scoutingLevel,
		shuffleRosters,
		teamInfos,
		teamSeasons,
		teamStats,
		teams,
	});
	// console.timeEnd("createStream");
};

export default createStream;
